// src/features/business-functions/hooks/useWorkspaceDetail.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { workspaceService } from '../services/workspaceService';
import {
  adaptLeadsFormsToTables,
  adaptWorkspaceDetail,
  adaptWorkspaceDetailFromForms,
  extractLeadFormRecordMeta,
} from '../adapters/workspaceDetailAdapter';
import { CAMPAIGN_COPY } from '../constants/campaignCopy';
import { workspaceDetailAtom, workspaceTablesAtom } from '../state/workspaceAtoms';

function isCachedForWorkspace(cachedWorkspace, workspaceId) {
  if (!cachedWorkspace || !workspaceId) return false;
  return String(cachedWorkspace.id) === String(workspaceId);
}

/**
 * Loads workspace metadata + form tables.
 * Schema (header/tabs) resolves quickly; record counts refresh in the background.
 */
export default function useWorkspaceDetail() {
  const { workspaceId, accountId } = useParams();
  const loadedRef = useRef(null);
  const countsRequestRef = useRef(0);

  const [workspaceAtom, setWorkspaceAtom] = useRecoilState(workspaceDetailAtom);
  const [tablesAtom, setTablesAtom] = useRecoilState(workspaceTablesAtom);

  const hasValidCache = isCachedForWorkspace(workspaceAtom, workspaceId);

  const [workspace, setWorkspace] = useState(hasValidCache ? workspaceAtom : null);
  const [tables, setTables] = useState(hasValidCache ? tablesAtom : []);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(!hasValidCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;

    countsRequestRef.current += 1;

    const cacheValid = isCachedForWorkspace(workspaceAtom, workspaceId);
    if (!cacheValid) {
      setWorkspace(null);
      setTables([]);
      setIsLoadingWorkspace(true);
    }
    setError(null);
  }, [workspaceId, workspaceAtom]);

  const fetchRecordCountsInBackground = useCallback(async (formsPayload, formsList, requestId) => {
    const recordCountsByFormId = {};

    await Promise.all(
      formsList.map(async (form) => {
        const formId = String(form.leads_form_id ?? form.id ?? '');
        if (!formId) return;

        try {
          const countRes = await workspaceService.getLeadFormRecordCount(formId, workspaceId);
          if (countRes?.status === 200 || countRes?.status === 201) {
            recordCountsByFormId[formId] = extractLeadFormRecordMeta(countRes.data);
          }
        } catch {
          recordCountsByFormId[formId] = { recordsCount: 0, updatedAt: '' };
        }
      })
    );

    if (countsRequestRef.current !== requestId) return;

    const tablesWithCounts = adaptLeadsFormsToTables(formsPayload, recordCountsByFormId);
    setTables(tablesWithCounts);
    setTablesAtom(tablesWithCounts);
    setWorkspace((prev) => (prev ? { ...prev, tablesCount: tablesWithCounts.length } : prev));
    setWorkspaceAtom((prev) => (prev ? { ...prev, tablesCount: tablesWithCounts.length } : prev));
  }, [workspaceId, setTablesAtom, setWorkspaceAtom]);

  const fetchWorkspace = useCallback(async () => {
    if (!workspaceId) {
      setWorkspace(null);
      setTables([]);
      setError(null);
      setIsLoadingWorkspace(false);
      return;
    }

    const isFirstLoad = loadedRef.current !== workspaceId;
    if (isFirstLoad && !isCachedForWorkspace(workspaceAtom, workspaceId)) {
      setIsLoadingWorkspace(true);
    }
    setError(null);

    const countsRequestId = countsRequestRef.current + 1;
    countsRequestRef.current = countsRequestId;

    try {
      let adaptedWorkspace = null;
      try {
        const detailRes = await workspaceService.getWorkspaceDetail(accountId, workspaceId);
        if (detailRes?.status === 200 || detailRes?.status === 201) {
          adaptedWorkspace = adaptWorkspaceDetail(detailRes.data, workspaceId);
        }
      } catch {
        adaptedWorkspace = null;
      }

      const tablesRes = await workspaceService.getWorkspaceTables(workspaceId);
      if (tablesRes?.status !== 200 && tablesRes?.status !== 201) {
        throw new Error(CAMPAIGN_COPY.errors.loadForms);
      }

      const formsPayload = tablesRes.data;
      if (!adaptedWorkspace) {
        adaptedWorkspace = adaptWorkspaceDetailFromForms(formsPayload, workspaceId);
      }

      const formsList = Object.values(formsPayload ?? {}).filter(Boolean);
      const adaptedTables = adaptLeadsFormsToTables(formsPayload, {});

      adaptedWorkspace = {
        ...adaptedWorkspace,
        tablesCount: adaptedTables.length,
      };

      setWorkspace(adaptedWorkspace);
      setWorkspaceAtom(adaptedWorkspace);
      setTables(adaptedTables);
      setTablesAtom(adaptedTables);
      setIsLoadingWorkspace(false);

      void fetchRecordCountsInBackground(formsPayload, formsList, countsRequestId);
    } catch (err) {
      setWorkspace(null);
      setTables([]);
      setWorkspaceAtom(null);
      setTablesAtom([]);
      setError(
        err?.response?.data?.message
        || err?.message
        || CAMPAIGN_COPY.errors.loadDetail
      );
      setIsLoadingWorkspace(false);
    } finally {
      loadedRef.current = workspaceId;
    }
  }, [
    workspaceId,
    accountId,
    setWorkspaceAtom,
    setTablesAtom,
    fetchRecordCountsInBackground,
  ]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  return {
    workspaceId,
    accountId,
    workspace,
    tables,
    hasTables: tables.length > 0,
    isLoadingWorkspace,
    /** @deprecated use isLoadingWorkspace */
    isLoading: isLoadingWorkspace,
    error,
    refetch: fetchWorkspace,
  };
}
